import {
	Check,
	CheckCircle,
	CircleCheckBig,
	FolderPlus,
	FolderSymlink,
	Mail,
	MailCheck,
	MailOpen,
	MailWarning,
	Phone,
	Tag,
	User,
} from 'lucide-react';

import {
	ContextMenuCheckboxItem,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuLabel,
	ContextMenuSeparator,
	ContextMenuSub,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import ConversationPinMenuItems from './ConversationPinMenuItems';
import { getFolderTitle, useConversationQuickActions } from './inbox-mobile/useConversationQuickActions';

// Touch screens (an iPad) can't reveal the hover checkbox over the avatar, so
// the long-press menu offers selection instead.
const isCoarsePointer = () =>
	typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

const CustomerConversationContextMenuContent = ({
	conversation,
	onCreateFolder,
	onShowVoiceUpsell,
	onSelectConversation,
}) => {
	const {
		auth,
		_business,
		customer,
		folders,
		isRead,
		customerOnActiveCall,
		isCallDisabled,
		hasChannelChoice,
		preferredChannel,
		currentTags,
		availableTags,
		activeUsers,
		businessDepartments,
		selectedAssignees,
		displayNotification,
		toggleConversationPin,
		handleToggleRead,
		handleToggleOpen,
		handleCallCustomer,
		handleSelectAssignees,
		handleToggleUserAssignee,
		handleToggleDepartmentAssignee,
		handleToggleCustomerTag,
		handleFolderAction,
		handleRemoveFolder,
		handleSelectPreferredChannel,
	} = useConversationQuickActions(conversation, { onShowVoiceUpsell });

	return (
		<ContextMenuContent className="w-56">
			{onSelectConversation && isCoarsePointer() && (
				<>
					<ContextMenuItem className="gap-2" onSelect={onSelectConversation}>
						<CircleCheckBig className="size-4" />
						Select
					</ContextMenuItem>
					<ContextMenuSeparator />
				</>
			)}
			{customer?.phone && (
				<>
					<ContextMenuItem
						className="gap-2"
						disabled={isCallDisabled}
						onSelect={handleCallCustomer}
					>
						<Phone className="size-4" />
						{customerOnActiveCall
							? 'Customer is already on a call'
							: 'Call customer'}
					</ContextMenuItem>
					<ContextMenuSeparator />
				</>
			)}

			<ContextMenuItem className="gap-2" onSelect={handleToggleRead}>
				{isRead ? <MailWarning className="size-4" /> : <MailCheck className="size-4" />}
				{isRead ? 'Mark as unread' : 'Mark as read'}
			</ContextMenuItem>
			<ContextMenuItem className="gap-2" onSelect={handleToggleOpen}>
				{conversation.open ? (
					<CheckCircle className="size-4" />
				) : (
					<MailOpen className="size-4" />
				)}
				{conversation.open ? 'Close conversation' : 'Reopen conversation'}
			</ContextMenuItem>

			<ContextMenuSeparator />
			<ConversationPinMenuItems
				auth={auth}
				conversation={conversation}
				displayNotification={displayNotification}
				ItemComponent={ContextMenuItem}
				toggleConversationPin={toggleConversationPin}
			/>

			<ContextMenuSeparator />
			<ContextMenuSub>
				<ContextMenuSubTrigger className="gap-2" disabled={!_business}>
					<User className="size-4" />
					Assign
				</ContextMenuSubTrigger>
				<ContextMenuSubContent className="w-56 max-h-[min(24rem,var(--radix-context-menu-content-available-height))] overflow-y-auto">
					<ContextMenuCheckboxItem
						checked={selectedAssignees.length === 0}
						onCheckedChange={() => handleSelectAssignees([])}
						onSelect={(event) => event.preventDefault()}
					>
						Unassigned
					</ContextMenuCheckboxItem>
					{activeUsers.length > 0 && (
						<>
							<ContextMenuSeparator />
							<ContextMenuLabel>Users</ContextMenuLabel>
							{activeUsers.map((user) => {
								const value = `USER_${user._id}`;
								return (
									<ContextMenuCheckboxItem
										key={user._id}
										checked={selectedAssignees.includes(value)}
										onCheckedChange={() => handleToggleUserAssignee(user._id)}
										onSelect={(event) => event.preventDefault()}
									>
										<span className="truncate">{user.name || user.username}</span>
									</ContextMenuCheckboxItem>
								);
							})}
						</>
					)}
					{businessDepartments.length > 0 && (
						<>
							<ContextMenuSeparator />
							<ContextMenuLabel>Departments</ContextMenuLabel>
							{businessDepartments.map((department) => {
								const value = `DEPARTMENT_${department._id}`;
								return (
									<ContextMenuCheckboxItem
										key={department._id}
										checked={selectedAssignees.includes(value)}
										onCheckedChange={() =>
											handleToggleDepartmentAssignee(department._id)
										}
										onSelect={(event) => event.preventDefault()}
									>
										<span className="truncate">{department.name}</span>
									</ContextMenuCheckboxItem>
								);
							})}
						</>
					)}
				</ContextMenuSubContent>
			</ContextMenuSub>
			<ContextMenuSub>
				<ContextMenuSubTrigger className="gap-2" disabled={!customer}>
					<Tag className="size-4" />
					Tags
				</ContextMenuSubTrigger>
				<ContextMenuSubContent className="w-56 max-h-[min(24rem,var(--radix-context-menu-content-available-height))] overflow-y-auto">
					{availableTags.length ? (
						availableTags.map((tag) => (
							<ContextMenuCheckboxItem
								key={tag}
								checked={currentTags.includes(tag)}
								onCheckedChange={() => handleToggleCustomerTag(tag)}
								onSelect={(event) => event.preventDefault()}
							>
								<span className="truncate">{tag}</span>
							</ContextMenuCheckboxItem>
						))
					) : (
						<ContextMenuItem disabled>No tags available</ContextMenuItem>
					)}
				</ContextMenuSubContent>
			</ContextMenuSub>
			<ContextMenuSub>
				<ContextMenuSubTrigger className="gap-2">
					<FolderSymlink className="size-4" />
					{conversation._folder
						? getFolderTitle(folders.find((folder) => folder._id === conversation._folder)) ||
							'Move to folder'
						: 'Move to folder'}
				</ContextMenuSubTrigger>
				<ContextMenuSubContent className="w-56">
					{folders.map((folder) => (
						<ContextMenuItem
							key={folder._id}
							onSelect={() => handleFolderAction(folder._id)}
						>
							<span className="truncate">{getFolderTitle(folder)}</span>
							{conversation._folder === folder._id && <Check className="ml-auto size-4" />}
						</ContextMenuItem>
					))}
					{folders.length > 0 && <ContextMenuSeparator />}
					<ContextMenuItem className="gap-2" onSelect={onCreateFolder}>
						<FolderPlus className="size-4" />
						Create folder
					</ContextMenuItem>
					{conversation._folder && (
						<>
							<ContextMenuSeparator />
							<ContextMenuItem onSelect={() => handleRemoveFolder(conversation._folder)}>
								Remove from folder
							</ContextMenuItem>
						</>
					)}
				</ContextMenuSubContent>
			</ContextMenuSub>

			{hasChannelChoice && (
				<>
					<ContextMenuSeparator />
					<ContextMenuItem
						className="gap-2"
						onSelect={() =>
							handleSelectPreferredChannel(
								preferredChannel.toLowerCase() === 'phone' ? 'email' : 'phone',
							)
						}
					>
						{preferredChannel.toLowerCase() === 'phone' ? (
							<Mail className="size-4" />
						) : (
							<Phone className="size-4" />
						)}
						Switch to {preferredChannel.toLowerCase() === 'phone' ? 'email' : 'phone'}
					</ContextMenuItem>
				</>
			)}
		</ContextMenuContent>
	);
};

export default CustomerConversationContextMenuContent;
